import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { 
  ArrowLeft, Search, FolderOpen, FileCode, Play, Eye, User,
  Clock, CheckCircle, XCircle, AlertCircle, Tag
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SharedDashboard() {
  const navigate = useNavigate();
  const { teamId } = useParams();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [team, setTeam] = useState(null);
  const [sharedTestCases, setSharedTestCases] = useState([]);
  const [sharedSuites, setSharedSuites] = useState([]);
  const [executions, setExecutions] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("test-cases");

  useEffect(() => {
    fetchTeamData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamId]);

  const fetchTeamData = async () => {
    try {
      // Fetch team details
      const teamRes = await axios.get(`${API}/teams/${teamId}`, { withCredentials: true });
      setTeam(teamRes.data.team);
      
      // Fetch shared test cases (team members' shared test cases)
      const casesRes = await axios.get(`${API}/test-cases?team_id=${teamId}`, { withCredentials: true });
      setSharedTestCases(casesRes.data.filter(tc => tc.is_shared_with_team || tc.team_id === teamId));
      
      // Fetch shared suites
      const suitesRes = await axios.get(`${API}/suites?team_id=${teamId}`, { withCredentials: true });
      setSharedSuites(suitesRes.data.filter(s => s.is_shared_with_team || s.team_id === teamId));
      
      // Fetch recent executions for team
      const execRes = await axios.get(`${API}/executions`, { withCredentials: true });
      // Filter executions that belong to shared test cases
      const sharedTestIds = casesRes.data.map(tc => tc.id);
      setExecutions(execRes.data.filter(e => sharedTestIds.includes(e.test_case_id)));
      
    } catch (error) {
      console.error('Failed to fetch team data:', error);
      toast.error('Failed to load shared dashboard');
    } finally {
      setLoading(false);
    }
  };

  const runTest = async (testCaseId) => {
    try {
      toast.info('Starting test execution...');
      const response = await axios.post(`${API}/executions?test_case_id=${testCaseId}`, {}, { withCredentials: true });
      toast.success('Test started!');
      navigate(`/execution/${response.data.id}`);
    } catch (error) {
      console.error('Failed to start test:', error);
      toast.error('Failed to start test');
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'failed':
        return <XCircle className="w-4 h-4 text-red-500" />;
      case 'running':
        return <AlertCircle className="w-4 h-4 text-yellow-500 animate-pulse" />;
      default:
        return <Clock className="w-4 h-4 text-slate-500" />;
    }
  };

  const filteredTestCases = sharedTestCases.filter(tc => 
    tc.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    tc.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredSuites = sharedSuites.filter(s => 
    s.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f8f9fb] text-slate-900 flex items-center justify-center">
        <p className="text-slate-400">Loading shared dashboard...</p>
      </div>
    );
  }

  return (
    <TooltipProvider delayDuration={200}>
    <div className="min-h-screen bg-[#f8f9fb] text-slate-900" data-testid="shared-dashboard-page">
      <div className="fixed inset-0 hero-glow" />
      
      <div className="relative z-10">
        <div className="container mx-auto px-4 py-8 max-w-7xl">
          {/* Header */}
          <div className="flex items-center gap-4 mb-8">
            <Button
              variant="ghost"
              className="text-slate-400 hover:text-white"
              onClick={() => navigate('/teams')}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Teams
            </Button>
          </div>

          <div className="mb-8">
            <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Unbounded, sans-serif' }}>
              {team?.name} - Shared Dashboard
            </h1>
            <p className="text-slate-400">{team?.description || 'Team shared test cases and execution history'}</p>
          </div>

          {/* Search */}
          <div className="mb-6">
            <div className="relative w-full max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-500" />
              <Input
                placeholder="Search shared content..."
                className="pl-10 bg-[#0B0F1A] border-white/10 text-white"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                data-testid="shared-search-input"
              />
            </div>
          </div>

          {/* Content Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="bg-black/40 mb-6">
              <TabsTrigger value="test-cases" data-testid="shared-test-cases-tab" className="flex items-center gap-2">
                <FileCode className="w-4 h-4" />
                Test Cases ({filteredTestCases.length})
              </TabsTrigger>
              <TabsTrigger value="suites" data-testid="shared-suites-tab" className="flex items-center gap-2">
                <FolderOpen className="w-4 h-4" />
                Suites ({filteredSuites.length})
              </TabsTrigger>
              <TabsTrigger value="history" data-testid="shared-run-history-tab" className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Run History ({executions.length})
              </TabsTrigger>
            </TabsList>

            {/* Test Cases Tab */}
            <TabsContent value="test-cases">
              {filteredTestCases.length === 0 ? (
                <Card className="glass-card border-white/5 rounded-xl">
                  <CardContent className="p-12 text-center">
                    <FileCode className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold mb-2">No shared test cases</h3>
                    <p className="text-slate-400">Team members haven't shared any test cases yet</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredTestCases.map((testCase, idx) => (
                    <motion.div
                      key={testCase.id}
                      data-testid={`shared-tc-card-${testCase.id}`}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: idx * 0.05 }}
                    >
                      <Card className="glass-card border-white/5 rounded-xl hover:border-white/20 transition-colors">
                        <CardHeader>
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <CardTitle className="text-lg mb-1">{testCase.name}</CardTitle>
                              <CardDescription className="line-clamp-2">
                                {testCase.description || 'No description'}
                              </CardDescription>
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent>
                          {/* Tags */}
                          {testCase.tags && testCase.tags.length > 0 && (
                            <div className="flex flex-wrap gap-1 mb-3">
                              {testCase.tags.slice(0, 3).map((tag) => (
                                <Badge key={tag} className="bg-[#D946EF]/10 text-[#D946EF] border-[#D946EF]/20 text-xs">
                                  <Tag className="w-3 h-3 mr-1" />
                                  {tag}
                                </Badge>
                              ))}
                              {testCase.tags.length > 3 && (
                                <Badge className="bg-white/5 text-slate-400 text-xs">+{testCase.tags.length - 3}</Badge>
                              )}
                            </div>
                          )}

                          {/* Shared by */}
                          <div className="flex items-center gap-2 mb-4 text-sm text-slate-400">
                            <User className="w-4 h-4" />
                            <span>Shared by team member</span>
                          </div>

                          {/* Actions */}
                          <div className="flex gap-2">
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="flex-1 border-white/10"
                                  onClick={() => navigate(`/edit-test/${testCase.id}`)}
                                >
                                  <Eye className="w-4 h-4 mr-1" />
                                  View
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>View test case details</TooltipContent>
                            </Tooltip>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  size="sm"
                                  className="flex-1 bg-[#00E599] text-black hover:bg-[#00CC88]"
                                  onClick={() => runTest(testCase.id)}
                                >
                                  <Play className="w-4 h-4 mr-1" />
                                  Run
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Run this test</TooltipContent>
                            </Tooltip>
                          </div>
                        </CardContent>
                      </Card>
                    </motion.div>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Suites Tab */}
            <TabsContent value="suites">
              {filteredSuites.length === 0 ? (
                <Card className="glass-card border-white/5 rounded-xl">
                  <CardContent className="p-12 text-center">
                    <FolderOpen className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold mb-2">No shared suites</h3>
                    <p className="text-slate-400">Team leader hasn't created any suites yet</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {filteredSuites.map((suite, idx) => (
                    <motion.div
                      key={suite.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: idx * 0.05 }}
                    >
                      <Card className="glass-card border-white/5 rounded-xl hover:border-white/20 transition-colors">
                        <CardHeader>
                          <div className="flex items-start gap-3">
                            <div className="w-12 h-12 bg-[#F59E0B]/10 rounded-lg flex items-center justify-center">
                              <FolderOpen className="w-6 h-6 text-[#F59E0B]" />
                            </div>
                            <div className="flex-1">
                              <CardTitle className="text-lg">{suite.name}</CardTitle>
                              <CardDescription>{suite.description || 'No description'}</CardDescription>
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent>
                          <div className="flex items-center justify-between">
                            <Badge className="bg-[#F59E0B]/10 text-[#F59E0B] border-[#F59E0B]/20">
                              {suite.test_case_ids?.length || 0} tests
                            </Badge>
                            <Button
                              size="sm"
                              className="bg-[#00E599] text-black hover:bg-[#00CC88]"
                              onClick={() => navigate('/suites')}
                            >
                              <Play className="w-4 h-4 mr-1" />
                              Run Suite
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    </motion.div>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Run History Tab */}
            <TabsContent value="history">
              {executions.length === 0 ? (
                <Card className="glass-card border-white/5 rounded-xl">
                  <CardContent className="p-12 text-center">
                    <Clock className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold mb-2">No execution history</h3>
                    <p className="text-slate-400">Run some tests to see the history here</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-4">
                  {executions.slice(0, 20).map((execution, idx) => {
                    const testCase = sharedTestCases.find(tc => tc.id === execution.test_case_id);
                    return (
                      <motion.div
                        key={execution.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: idx * 0.03 }}
                      >
                        <Card
                          className="glass-card border-white/5 rounded-xl hover:border-white/20 transition-colors cursor-pointer"
                          onClick={() => navigate(`/execution/${execution.id}`)}
                        >
                          <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-4">
                                {getStatusIcon(execution.status)}
                                <div>
                                  <p className="font-medium">{testCase?.name || 'Unknown Test'}</p>
                                  <p className="text-sm text-slate-400">
                                    {new Date(execution.start_time).toLocaleString()}
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center gap-3">
                                <Badge className={
                                  execution.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                                  execution.status === 'failed' ? 'bg-red-500/20 text-red-400' :
                                  execution.status === 'running' ? 'bg-yellow-500/20 text-yellow-400' :
                                  'bg-white/10 text-slate-400'
                                }>
                                  {execution.status}
                                </Badge>
                                <Button size="sm" variant="ghost" className="text-slate-400 hover:text-white">
                                  <Eye className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      </motion.div>
                    );
                  })}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
    </TooltipProvider>
  );
}
